import { formatPrice } from '@/lib/format';
import { normalizeSearch } from '@/lib/search';
import { format } from 'date-fns';
import type { MobileLine, TravelEsimTariff } from '@octave/contracts';

/** Data allowance label: « 3 Go », « Internet illimité » (never "GB", per the brief). */
export function formatTravelEsimData(dataLimitGb: number | null): string {
  return dataLimitGb === null ? 'Internet illimité' : `${dataLimitGb} Go`;
}

/** « 7 jours », « 1 jour ». */
export function formatTravelEsimDuration(days: number): string {
  return `${days} ${days > 1 ? 'jours' : 'jour'}`;
}

/**
 * Case- and accent-insensitive match of the search on the French or English
 * destination. An empty search keeps everything.
 */
export function filterTravelEsimTariffs(
  tariffs: TravelEsimTariff[],
  search: string,
): TravelEsimTariff[] {
  const needle = normalizeSearch(search);
  if (needle === '') return tariffs;
  return tariffs.filter(
    (t) =>
      normalizeSearch(t.country).includes(needle) ||
      (t.countryEn !== null && normalizeSearch(t.countryEn).includes(needle)),
  );
}

/** Number of distinct destinations in a set of rows. */
export function countTravelEsimDestinations(
  tariffs: TravelEsimTariff[],
): number {
  return new Set(tariffs.map((t) => t.country)).size;
}

/** « Internet illimité » or « 3 Go de data ». */
export function formatTravelEsimDataBullet(dataLimitGb: number | null): string {
  return dataLimitGb === null
    ? 'Internet illimité'
    : `${dataLimitGb} Go de data`;
}

const MS_PER_DAY = 86_400_000;

/**
 * Inclusive day count between two `YYYY-MM-DD` dates (departure and return
 * both count), computed in UTC so DST never shifts it. Mirrors the server.
 */
export function computeStayDurationDays(
  departureDate: string,
  returnDate: string,
): number {
  const [dy, dm, dd] = departureDate.split('-').map(Number);
  const [ry, rm, rd] = returnDate.split('-').map(Number);
  return (
    Math.round((Date.UTC(ry, rm - 1, rd) - Date.UTC(dy, dm - 1, dd)) / MS_PER_DAY) +
    1
  );
}

/** Today as `YYYY-MM-DD`, in the browser's time zone (the calendar's). */
export function todayIsoDate(now: Date = new Date()): string {
  return format(now, 'yyyy-MM-dd');
}

/**
 * A stay leaving before today cannot be ordered (the server refuses it):
 * a stale one, from an old link or a tab left open overnight, is dropped.
 */
export function upcomingStay(
  departureDate: string | undefined,
  returnDate: string | undefined,
  today: string = todayIsoDate(),
): { departureDate?: string; returnDate?: string } {
  if (departureDate && departureDate < today) return {};
  return { departureDate, returnDate };
}

/** Holder display name of a line, falling back to its number. */
export function formatLineHolder(line: MobileLine): string {
  const name = line.identity
    ? `${line.identity.firstName} ${line.identity.lastName}`.trim()
    : '';
  return name || line.number;
}

/**
 * The fleet line whose holder already uses this e-mail, if any. Used for the
 * non-blocking "already in the fleet" alert of the off-fleet beneficiary.
 */
export function findFleetDuplicate(
  lines: MobileLine[],
  email: string,
): MobileLine | null {
  const needle = email.trim().toLowerCase();
  if (needle === '') return null;
  return (
    lines.find((line) => line.identity?.email.trim().toLowerCase() === needle) ??
    null
  );
}

/** « 2,88 € / jour ». */
export function formatTravelEsimPricePerDay(pricePerDayCents: number): string {
  return `${formatPrice(pricePerDayCents)} / jour`;
}

export interface TravelEsimLineStatus {
  label: string;
  tone: 'pending' | 'ready' | 'done';
}

/**
 * Where an eSIM order line stands for the client. A new eSIM goes through
 * the QR code (awaited, ready, sent); a recharge is loaded by Octave on the
 * existing eSIM, no QR involved.
 */
export function travelEsimLineStatus(metadata: {
  orderKind?: 'CREATION' | 'RECHARGE' | null;
  qrCodePath?: string | null;
  qrCodeSentAt?: string | null;
  planLoadedAt?: string | null;
}): TravelEsimLineStatus {
  if (metadata.orderKind === 'RECHARGE') {
    return metadata.planLoadedAt
      ? { label: 'Forfait chargé', tone: 'done' }
      : { label: 'Forfait à charger', tone: 'pending' };
  }
  if (metadata.qrCodeSentAt) return { label: 'QR code envoyé', tone: 'done' };
  if (metadata.qrCodePath) {
    return { label: 'QR code prêt, pas encore envoyé', tone: 'ready' };
  }
  return { label: 'QR code en attente', tone: 'pending' };
}

/** Tailwind classes of a status pill (pending amber, ready sky, done emerald). */
export function travelEsimStatusClasses(tone: TravelEsimLineStatus['tone']): string {
  switch (tone) {
    case 'done':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'ready':
      return 'bg-sky-50 text-sky-700 border-sky-200';
    default:
      return 'bg-amber-50 text-amber-700 border-amber-200';
  }
}
